import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { newIdempotencyKey } from '@/lib/idempotency';
import { trpc } from '@/lib/trpc';
import {
  type MatchSearchResult,
  SIGNAL_MANUAL_SOURCES,
  type SignalManualSource,
} from '@tennis-scanner/shared';
import { useState } from 'react';
import { toast } from 'sonner';

type Selection = 'p1' | 'p2';

interface Props {
  match: MatchSearchResult;
  onCreated: () => void;
}

// Form for signals.createManual. Lives in its own file to keep the parent
// page under 300 LOC. Source defaults to 'winnerodds' because that's the
// dominant flow described by the operator (manual export of WO signals).
export function ManualPickForm({ match, onCreated }: Props) {
  const [market, setMarket] = useState('h2h');
  const [selection, setSelection] = useState<Selection>('p1');
  const [targetOdds, setTargetOdds] = useState('');
  const [validUntilMinutes, setValidUntilMinutes] = useState('240');
  const [source, setSource] = useState<SignalManualSource>('winnerodds');
  const [notes, setNotes] = useState('');
  const [manualStakeUsd, setManualStakeUsd] = useState('');

  const utils = trpc.useUtils();
  const createManual = trpc.signals.createManual.useMutation({
    onSuccess: async () => {
      toast.success('Signal manual creado', {
        description: `${match.p1Name ?? 'p1'} vs ${match.p2Name ?? 'p2'}`,
      });
      await utils.opportunities.list.invalidate();
      await utils.signals.list.invalidate();
      // Reset only the per-pick fields. Keep market/source/validUntilMinutes
      // so chained picks are fast (operator typically loads many picks of
      // the same shape from a single source in one session).
      setSelection('p1');
      setTargetOdds('');
      setNotes('');
      setManualStakeUsd('');
      onCreated();
    },
    onError: (err) => toast.error('No se pudo crear', { description: err.message }),
  });

  const oddsNum = Number.parseFloat(targetOdds);
  const minutesNum = Number.parseInt(validUntilMinutes, 10);
  const stakeNum = manualStakeUsd.trim() === '' ? null : Number.parseFloat(manualStakeUsd);
  const canSubmit =
    market.trim().length > 0 &&
    Number.isFinite(oddsNum) &&
    oddsNum >= 1.01 &&
    oddsNum <= 1000 &&
    Number.isInteger(minutesNum) &&
    minutesNum >= 15 &&
    minutesNum <= 60 * 24 * 14 &&
    (stakeNum === null || (Number.isFinite(stakeNum) && stakeNum >= 0 && stakeNum <= 1_000_000));

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    createManual.mutate({
      canonicalMatchId: match.id,
      market: market.trim(),
      selection,
      targetOdds: oddsNum,
      validUntilMinutes: minutesNum,
      source,
      notes: notes.trim() || undefined,
      manualStakeUsd: stakeNum ?? undefined,
      idempotencyKey: newIdempotencyKey(),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
      <div className="grid gap-1.5">
        <Label htmlFor="m-market">Mercado</Label>
        <Input
          id="m-market"
          value={market}
          onChange={(e) => setMarket(e.target.value)}
          placeholder="h2h"
          maxLength={32}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="m-selection">Selección</Label>
        <Select value={selection} onValueChange={(v) => setSelection(v as Selection)}>
          <SelectTrigger id="m-selection">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="p1">p1 — {match.p1Name ?? 'Player 1'}</SelectItem>
            <SelectItem value="p2">p2 — {match.p2Name ?? 'Player 2'}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="m-odds">Target odds (3 decimales)</Label>
        <Input
          id="m-odds"
          type="number"
          step="0.001"
          min="1.01"
          max="1000"
          value={targetOdds}
          onChange={(e) => setTargetOdds(e.target.value)}
          placeholder="1.953"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="m-window">Válido (minutos)</Label>
        <Input
          id="m-window"
          type="number"
          step="1"
          min="15"
          max={60 * 24 * 14}
          value={validUntilMinutes}
          onChange={(e) => setValidUntilMinutes(e.target.value)}
        />
        <p className="text-[10px] font-mono text-muted-foreground">
          el server extiende el plazo hasta el inicio del partido + 30min si tu valor es menor.
        </p>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="m-source">Fuente</Label>
        <Select value={source} onValueChange={(v) => setSource(v as SignalManualSource)}>
          <SelectTrigger id="m-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SIGNAL_MANUAL_SOURCES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="m-stake">
          Stake USD{' '}
          <span className="text-[10px] font-mono text-muted-foreground">
            (opcional — se muestra junto al sugerido por Kelly)
          </span>
        </Label>
        <Input
          id="m-stake"
          type="number"
          step="1"
          min="0"
          max={1_000_000}
          value={manualStakeUsd}
          onChange={(e) => setManualStakeUsd(e.target.value)}
          placeholder="—"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="m-notes">Nota (opcional)</Label>
        <Input
          id="m-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
        />
      </div>
      <div className="flex justify-end md:col-span-2">
        <Button type="submit" disabled={!canSubmit || createManual.isPending}>
          {createManual.isPending ? 'Creando…' : 'Crear pick'}
        </Button>
      </div>
    </form>
  );
}
